/**
 * Dress My Angel - Custom Boutique Features
 * Handles LocalStorage-based Wishlist and Recently Viewed Products
 */

const DMA_CONFIG = {
  wishlistKey: 'dma_wishlist',
  recentlyViewedKey: 'dma_recently_viewed',
  maxRecentlyViewed: 4
};

// --- Currency Formatter Helper ---
function formatPrice(cents, currencyCode) {
  let dollars = (cents / 100).toFixed(2);
  
  // Format based on standard currency formats
  let symbol = '$';
  if (currencyCode === 'INR') symbol = '₹';
  else if (currencyCode === 'GBP') symbol = '£';
  else if (currencyCode === 'EUR') symbol = '€';
  
  // Clean up trailing .00 if desired, or keep standard
  return symbol + dollars;
}

// --- Recently Viewed Products Logic ---
function initRecentlyViewed() {
  const container = document.getElementById('RecentlyViewedGrid');
  const productSelects = document.querySelector('variant-selects');
  
  // 1. If we are on a product page, save the handle
  if (productSelects) {
    const currentHandle = productSelects.getAttribute('data-product-handle');
    if (currentHandle) {
      let recentlyViewed = JSON.parse(localStorage.getItem(DMA_CONFIG.recentlyViewedKey)) || [];
      // Remove current handle if already exists to move it to front
      recentlyViewed = recentlyViewed.filter(h => h !== currentHandle);
      recentlyViewed.unshift(currentHandle);
      // Limit size
      if (recentlyViewed.length > 8) {
        recentlyViewed = recentlyViewed.slice(0, 8);
      }
      localStorage.setItem(DMA_CONFIG.recentlyViewedKey, JSON.stringify(recentlyViewed));
    }
  }
  
  // 2. If the container exists, render recently viewed items
  if (container) {
    let recentlyViewed = JSON.parse(localStorage.getItem(DMA_CONFIG.recentlyViewedKey)) || [];
    
    // Filter out current product if on a product page
    if (productSelects) {
      const currentHandle = productSelects.getAttribute('data-product-handle');
      recentlyViewed = recentlyViewed.filter(h => h !== currentHandle);
    }
    
    // Limit display items
    recentlyViewed = recentlyViewed.slice(0, DMA_CONFIG.maxRecentlyViewed);
    
    if (recentlyViewed.length === 0) {
      // Hide the section if empty
      const section = container.closest('.recently-viewed-section');
      if (section) section.style.display = 'none';
      return;
    }
    
    container.innerHTML = '<div class="loading-spinner-center"><div class="loading__spinner"></div></div>';
    
    // Fetch product details via AJAX
    const fetchPromises = recentlyViewed.map(handle => 
      fetch(`/products/${handle}.js`)
        .then(response => {
          if (!response.ok) throw new Error('Product not found');
          return response.json();
        })
        .catch(err => null)
    );
    
    Promise.all(fetchPromises).then(products => {
      // Filter out failed requests
      const validProducts = products.filter(p => p !== null);
      
      if (validProducts.length === 0) {
        const section = container.closest('.recently-viewed-section');
        if (section) section.style.display = 'none';
        return;
      }
      
      container.innerHTML = '';
      
      validProducts.forEach(product => {
        const cardHtml = renderProductCardHtml(product);
        container.insertAdjacentHTML('beforeend', cardHtml);
      });
      
      // Wire up wishlist toggles in the new cards
      bindWishlistEvents();
      updateWishlistUI();
    });
  }
}

// --- Wishlist Logic ---
function getWishlist() {
  return JSON.parse(localStorage.getItem(DMA_CONFIG.wishlistKey)) || [];
}

function toggleWishlist(handle) {
  let wishlist = getWishlist();
  let added = false;
  if (wishlist.includes(handle)) {
    wishlist = wishlist.filter(h => h !== handle);
  } else {
    wishlist.push(handle);
    added = true;
  }
  localStorage.setItem(DMA_CONFIG.wishlistKey, JSON.stringify(wishlist));
  updateWishlistUI();
  return added;
}

function updateWishlistUI() {
  const wishlist = getWishlist();
  
  // Update all wishlist buttons state
  document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
    const handle = btn.getAttribute('data-wishlist-toggle');
    const heartIcon = btn.querySelector('.heart-icon');
    
    if (wishlist.includes(handle)) {
      btn.classList.add('in-wishlist');
      if (heartIcon) {
        // Change SVG to filled heart
        heartIcon.innerHTML = `
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
          </svg>
        `;
      }
    } else {
      btn.classList.remove('in-wishlist');
      if (heartIcon) {
        // Change SVG to outline heart
        heartIcon.innerHTML = `
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
          </svg>
        `;
      }
    }
  });

  // Update wishlist counts in header/navigation
  document.querySelectorAll('.wishlist-count-badge').forEach(badge => {
    badge.textContent = wishlist.length;
    if (wishlist.length > 0) {
      badge.style.display = 'inline-flex';
    } else {
      badge.style.display = 'none';
    }
  });
  
  // Render wishlist drawer list
  renderWishlistDrawer();
}

function bindWishlistEvents() {
  document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
    // Prevent duplicate binding
    if (btn.getAttribute('data-wishlist-bound')) return;
    btn.setAttribute('data-wishlist-bound', 'true');
    
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const handle = btn.getAttribute('data-wishlist-toggle');
      if (handle) {
        const added = toggleWishlist(handle);
        
        // Show floating notification (optional but nice UX)
        if (added && typeof openWishlistDrawer === 'function') {
          // Open wishlist drawer to show the item was added
          openWishlistDrawer();
        }
      }
    });
  });
}

function openWishlistDrawer() {
  const drawer = document.getElementById('WishlistDrawer');
  if (drawer) {
    drawer.classList.add('active');
    document.body.style.overflow = 'hidden';
  }
}

function closeWishlistDrawer() {
  const drawer = document.getElementById('WishlistDrawer');
  if (drawer) {
    drawer.classList.remove('active');
    document.body.style.overflow = '';
  }
}

function renderWishlistDrawer() {
  const container = document.getElementById('WishlistDrawerItems');
  if (!container) return;
  
  const wishlist = getWishlist();
  
  if (wishlist.length === 0) {
    container.innerHTML = `
      <div class="wishlist-drawer__empty">
        <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.5" style="opacity:0.5; margin-bottom:1rem;">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
        </svg>
        <p>Your wishlist is empty</p>
      </div>
    `;
    return;
  }
  
  container.innerHTML = '<div class="loading-spinner-center"><div class="loading__spinner"></div></div>';
  
  const fetchPromises = wishlist.map(handle => 
    fetch(`/products/${handle}.js`)
      .then(response => response.ok ? response.json() : null)
      .catch(() => null)
  );
  
  Promise.all(fetchPromises).then(products => {
    const validProducts = products.filter(p => p !== null);
    container.innerHTML = '';
    
    validProducts.forEach(product => {
      const price = formatPrice(product.price, window.Shopify?.currency?.active);
      const itemHtml = `
        <div class="wishlist-drawer-item" data-product-handle="${product.handle}">
          <div class="wishlist-drawer-item__image">
            <a href="${product.url}">
              <img src="${product.featured_image}" alt="${product.title}" width="80" height="100" loading="lazy">
            </a>
          </div>
          <div class="wishlist-drawer-item__details">
            <h4 class="wishlist-drawer-item__title">
              <a href="${product.url}">${product.title}</a>
            </h4>
            <div class="wishlist-drawer-item__price">${price}</div>
            <div class="wishlist-drawer-item__actions">
              <a href="${product.url}" class="button button--secondary button--small">View Product</a>
              <button type="button" class="wishlist-drawer-item__remove" onclick="toggleWishlist('${product.handle}')" aria-label="Remove item">
                Remove
              </button>
            </div>
          </div>
        </div>
      `;
      container.insertAdjacentHTML('beforeend', itemHtml);
    });
  });
}

// --- Dynamic HTML Product Card Renderer ---
function renderProductCardHtml(product) {
  const currencyCode = window.Shopify?.currency?.active || 'USD';
  const price = formatPrice(product.price, currencyCode);
  const compareAtPrice = product.compare_at_price ? formatPrice(product.compare_at_price, currencyCode) : '';
  const onSale = compareAtPrice ? 'price--on-sale' : '';
  
  return `
    <div class="grid__item card-wrapper product-card-wrapper underline-links-hover">
      <div class="card card--standard card--media">
        <div class="card__inner ratio" style="--ratio-percent: 125%;">
          <div class="card__media">
            <div class="media media--transparent media--hover-effect">
              <a href="${product.url}">
                <img src="${product.featured_image}" alt="${product.title}" class="motion-reduce" loading="lazy" width="533" height="666">
              </a>
            </div>
          </div>
          <!-- Wishlist Toggle overlay on image -->
          <button type="button" class="wishlist-overlay-btn" data-wishlist-toggle="${product.handle}" aria-label="Add to wishlist">
            <span class="heart-icon">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
              </svg>
            </span>
          </button>
        </div>
        <div class="card__content">
          <div class="card__information">
            <h3 class="card__heading h3">
              <a href="${product.url}" class="full-unstyled-link">${product.title}</a>
            </h3>
            <div class="price ${onSale}">
              <div class="price__container">
                <div class="price-item price-item--regular">
                  ${price}
                </div>
                ${compareAtPrice ? `<div class="price-item price-item--sale">${compareAtPrice}</div>` : ''}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

// --- Product Description List Formatting ---
function formatProductDescription() {
  try {
    const descriptions = document.querySelectorAll('.product__description.rte');
    descriptions.forEach(desc => {
      if (desc.getAttribute('data-formatted') === 'true') return;
      desc.setAttribute('data-formatted', 'true');
      
      const rawHtml = desc.innerHTML;
      if (!rawHtml) return;
      
      const lines = rawHtml.split(/<br\s*\/?>/i);
      let newHtml = '';
      let inList = false;
      
      lines.forEach(line => {
        const trimmed = line.trim();
        if (!trimmed) {
          if (inList) {
            newHtml += '</ul>';
            inList = false;
          }
          newHtml += '<br>';
          return;
        }
        
        const isHeading = trimmed.match(/^<h[1-6]/i) || trimmed.includes('description-heading');
        const plainText = trimmed.replace(/<[^>]*>/g, '').trim();
        const isBullet = !isHeading && plainText.match(/^[\-\*\u2022]/);
        const isProperty = !isHeading && plainText.includes(':') && !plainText.endsWith(':-') && !plainText.endsWith(':');
        
        if (isBullet || isProperty) {
          let cleanLine = trimmed.replace(/^([\-\*\u2022]\s*)/, '');
          if (!inList) {
            newHtml += '<ul class="custom-description-list">';
            inList = true;
          }
          newHtml += `<li>${cleanLine}</li>`;
        } else {
          if (inList) {
            newHtml += '</ul>';
            inList = false;
          }
          newHtml += line + '<br>';
        }
      });
      
      if (inList) {
        newHtml += '</ul>';
      }
      
      desc.innerHTML = newHtml;
    });
  } catch (e) {
    console.error("Error formatting product description:", e);
  }
}

// --- Initialization ---
document.addEventListener('DOMContentLoaded', () => {
  formatProductDescription();
  initRecentlyViewed();
  updateWishlistUI();
  bindWishlistEvents();
  
  // Wire up wishlist drawer close trigger
  const closeBtn = document.getElementById('CloseWishlistDrawer');
  if (closeBtn) {
    closeBtn.addEventListener('click', closeWishlistDrawer);
  }
});
